import { ORPCError } from '@orpc/client'
import type { Invitation, InvitationEligibility } from '@snitch/contract'
import { Alert } from '@snitch/ui/alert'
import { Button, buttonClass } from '@snitch/ui/button'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight, Check, House } from 'lucide-react'
import { api } from '../../../lib/api.ts'
import { signOut } from '../../../lib/auth.ts'
import {
  forgetInvitation,
  invitationTarget,
  inviteSource,
  normalizeInviteCode,
  rememberInvitation,
} from '../../../lib/invitations.ts'
import { inTelegram } from '../../../lib/telegram.ts'

export const Route = createFileRoute('/_authed/_shell/i/$code')({
  validateSearch: (s: Record<string, unknown>) => ({ via: inviteSource(s.via) }),
  component: InvitationPage,
})
const refusals: Partial<Record<InvitationEligibility, string>> = {
  self: 'Это ваш промокод. Поделитесь им с другом — собственное приглашение принять нельзя.',
  already_invited: 'У вас уже есть пригласивший. Заменить его другим человеком нельзя.',
  plan_purchased:
    'Вы уже купили обычный тариф. Приглашение можно принять только до первой такой покупки.',
  cycle: 'Нельзя принять взаимное приглашение.',
}

function invitationError(error: unknown, fallback: string) {
  return error instanceof ORPCError &&
    ['NOT_FOUND', 'PRECONDITION_FAILED', 'TOO_MANY_REQUESTS'].includes(error.code)
    ? error.message
    : fallback
}

function InvitationPage() {
  const { code: raw } = Route.useParams(),
    { via } = Route.useSearch()
  const { me } = Route.useRouteContext()
  const code = normalizeInviteCode(raw)
  const queryClient = useQueryClient(),
    navigate = useNavigate()
  const preview = useQuery({
    queryKey: ['referrals', 'preview', code, me?.user.id ?? 'guest'],
    queryFn: () => api.referrals.preview({ code: code ?? '' }),
    enabled: Boolean(code),
  })
  const mine = useQuery({
    queryKey: ['referrals', 'mine', me?.user.id],
    queryFn: () => api.referrals.mine(),
    enabled: Boolean(
      me && (preview.data?.eligibility === 'accepted' || preview.data?.eligibility === 'used'),
    ),
  })
  const accept = useMutation({
    mutationFn: (input: { code: string; source: 'web' | 'bot' | 'code' }) =>
      api.referrals.accept(input),
    onSuccess: async () => {
      forgetInvitation()
      await queryClient.invalidateQueries()
    },
  })
  const change = useMutation({
    mutationFn: async () => {
      if (!code) return
      rememberInvitation({ code, via })
      await signOut()
      queryClient.clear()
      await navigate({ to: '/login', search: { next: invitationTarget(code, via) } })
    },
  })
  const done =
    (accept.variables?.code === code ? accept.data : null) ??
    (preview.data?.eligibility === 'accepted' || preview.data?.eligibility === 'used'
      ? mine.data
      : null)
  const unavailable = !code || preview.isError
  const refusal = preview.data ? refusals[preview.data.eligibility] : undefined
  return (
    <main className="flex min-h-dvh flex-col gap-7 px-5 pt-5 pb-8 md:px-8 md:pt-7 xl:px-12 xl:pt-8">
      <Link
        to="/friends"
        onClick={forgetInvitation}
        className="flex min-h-11 w-fit items-center gap-2.5 text-ink-2 text-sm"
      >
        <span className="flex size-11 items-center justify-center rounded-full bg-card text-ink">
          <ArrowLeft className="size-5" aria-hidden="true" />
        </span>
        Друзья
      </Link>
      <div className="mx-auto flex w-full max-w-[560px] flex-col gap-[22px]">
        <h1 className="font-semibold text-[30px] leading-[1.3] tracking-[-0.6px] md:text-[32px]">
          {done ? 'Приглашение принято' : 'Приглашение от друга'}
        </h1>
        {done ? (
          <Accepted invitation={done} />
        ) : unavailable ? (
          <div className="flex flex-col gap-4 rounded-3xl border border-line bg-card p-6">
            <Alert tone="error">
              {invitationError(
                preview.error,
                code
                  ? 'Не удалось проверить приглашение. Попробуйте ещё раз.'
                  : 'Проверьте код или ссылку приглашения.',
              )}
            </Alert>
            <Button variant="soft" onClick={() => void preview.refetch()} disabled={!code}>
              Повторить
            </Button>
            <Link
              to="/profile/promos"
              onClick={forgetInvitation}
              className={buttonClass({ block: true })}
            >
              Ввести другой код
            </Link>
          </div>
        ) : preview.data ? (
          <section className="flex flex-col gap-5 rounded-3xl border border-line bg-card p-[22px] md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-sunken font-semibold text-[17px]">
                {preview.data.inviterName.charAt(0)}
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-[17px] [overflow-wrap:anywhere]">
                  {preview.data.inviterName}
                </p>
                <p className="text-ink-2 text-[13px]">Пригласил вас в Snitch</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-semibold text-[44px] tracking-[-1px]">
                −{preview.data.discountPercent}%
              </span>
              <span className="font-medium text-lg">
                на первый
                <br />
                обычный тариф
              </span>
            </div>
            <p className="break-all text-ink-2 text-[13px]">Промокод {preview.data.code}</p>
            <p className="text-ink-2 text-sm leading-[1.4]">
              Скидка только на тариф. Тест, LTE и дополнительные места — без этой скидки.
            </p>
            <div className="flex items-center justify-between gap-3 border-line border-t pt-5">
              <div className="min-w-0">
                <p className="text-ink-2 text-xs">Вы вошли как</p>
                <p className="truncate font-semibold">{me.user.displayName}</p>
              </div>
              {!inTelegram ? (
                <button
                  type="button"
                  className="min-h-11 px-2 font-semibold text-sm"
                  disabled={change.isPending}
                  onClick={() => change.mutate()}
                >
                  Сменить
                </button>
              ) : null}
            </div>
            {refusal ? <Alert tone="info">{refusal}</Alert> : null}
            {accept.isError ? (
              <Alert tone="error">
                {invitationError(
                  accept.error,
                  'Не получилось принять приглашение. Попробуйте ещё раз.',
                )}
              </Alert>
            ) : null}
            {change.isError ? (
              <Alert tone="error">Не удалось сменить аккаунт. Попробуйте ещё раз.</Alert>
            ) : null}
            {preview.data.eligibility === 'available' ? (
              <>
                <Button
                  block
                  size="md"
                  disabled={accept.isPending}
                  onClick={() => accept.mutate({ code: code ?? '', source: via })}
                >
                  {accept.isPending ? 'Принимаем…' : 'Принять приглашение'}
                  <ArrowRight />
                </Button>
                <p className="text-ink-2 text-[13px] leading-[1.4]">
                  Пригласивший закрепится за этим аккаунтом один раз.
                </p>
              </>
            ) : (
              <Link
                to="/friends"
                onClick={forgetInvitation}
                className={buttonClass({ block: true })}
              >
                К друзьям
              </Link>
            )}
          </section>
        ) : (
          <p role="status" className="text-ink-2 text-sm">
            Проверяем приглашение…
          </p>
        )}
      </div>
    </main>
  )
}
function Accepted({ invitation }: { invitation: Invitation }) {
  return (
    <section className="flex flex-col gap-5 rounded-3xl border border-line bg-card p-[22px] md:p-7">
      <span className="motion-success flex size-16 items-center justify-center rounded-full bg-kb-green-soft">
        <Check className="size-[30px] text-green-ink" aria-hidden="true" />
      </span>
      <p className="font-semibold text-lg [overflow-wrap:anywhere]">
        Вас пригласил {invitation.inviterName}
      </p>
      {invitation.used ? (
        <p className="text-ink-2">
          Скидка на первый тариф уже использована. Пригласивший остаётся прежним.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-4">
            <span className="font-semibold text-[40px]">−{invitation.discountPercent}%</span>
            <span className="font-medium text-[17px]">
              на первый
              <br />
              обычный тариф
            </span>
          </div>
          <p className="text-ink-2 text-[15px] leading-[1.4]">
            Скидка появится в расчёте автоматически. Тестовый тариф её не расходует.
          </p>
          <Link to="/plans" className={buttonClass({ block: true, size: 'md' })}>
            Выбрать тариф
            <ArrowRight />
          </Link>
        </>
      )}
      <Link to="/" className={buttonClass({ block: true, variant: 'soft', size: 'md' })}>
        На главную
        <House />
      </Link>
      {!invitation.used ? (
        <p className="text-ink-2 text-[13px] leading-[1.4]">
          Можно совместить с действующим акционным промокодом.
        </p>
      ) : null}
    </section>
  )
}
